import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCalendar, FiMessageCircle, FiZap, FiCheck, FiUser, FiMail, FiClock, FiArrowRight, FiLoader } = FiIcons;

// ─── Web3Forms access key ───────────────────────────────────────────────────
// Same key as your Careers form (from https://web3forms.com)
const WEB3FORMS_ACCESS_KEY = "YOUR_WEB3FORMS_ACCESS_KEY"; // ← replace this

const steps = [
  {
    icon: FiCalendar,
    title: "You request a time",
    desc: "Fill in the short form and tell us when works for you. We'll confirm your slot within a few hours."
  },
  {
    icon: FiMessageCircle,
    title: "We listen first",
    desc: "Tell us about your business, your challenge, and what you've already tried. We ask questions, we don't pitch."
  },
  {
    icon: FiZap,
    title: "You get honest advice",
    desc: "Whether we're the right fit or not, you'll leave the call with clarity on your options and a clear next step."
  }
];

const reassurances = [
  "No commitment required",
  "NDA signed before any sensitive discussion",
  "We work with businesses of all sizes"
];

const TIME_SLOTS = [
  "Morning (9 AM – 12 PM)",
  "Afternoon (12 PM – 3 PM)",
  "Late Afternoon (3 PM – 6 PM)",
  "Evening (6 PM – 8 PM)",
];

const DAYS = [
  "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Weekends OK"
];

const BookCall = () => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    timeSlot: '',
    preferredDays: [],
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const toggleDay = (day) => {
    setForm(prev => ({
      ...prev,
      preferredDays: prev.preferredDays.includes(day)
        ? prev.preferredDays.filter(d => d !== day)
        : [...prev.preferredDays, day]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const data = new FormData();
      data.append('access_key', WEB3FORMS_ACCESS_KEY);
      data.append('subject', `Call Request from ${form.name}${form.company ? ` — ${form.company}` : ''}`);
      data.append('name', form.name);
      data.append('email', form.email);
      data.append('company', form.company || 'Not provided');
      data.append('preferred_time', form.timeSlot);
      data.append('preferred_days', form.preferredDays.join(', ') || 'Not specified');
      data.append('message', form.message || '(no message provided)');

      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: data,
      });

      const json = await res.json();

      if (json.success) {
        setSubmitted(true);
      } else {
        setError(json.message || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-32 pb-24 relative overflow-hidden bg-white min-h-screen">
      <div className="absolute inset-0 bg-grid-pattern opacity-[0.15]"></div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-cyan-50 border border-cyan-200 mb-6">
            <div className="w-2 h-2 rounded-full bg-cyan-500 animate-pulse"></div>
            <span className="text-[10px] font-black uppercase tracking-[0.2em] text-cyan-600">Free 30-min Call</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-slate-900 mb-6 leading-tight">
            Let's Start With a <span className="text-gradient-cyan">Conversation</span>
          </h1>
          <p className="text-slate-600 text-lg md:text-xl leading-relaxed max-w-3xl mx-auto">
            No sales pitch. Just an honest conversation about your challenge and whether we're the right fit to help.
          </p>
        </motion.div>

        {/* Two-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">

          {/* Left — What to expect */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <h2 className="text-2xl font-bold text-slate-900 mb-10 uppercase tracking-widest border-l-4 border-cyan-500 pl-6">
              What to Expect
            </h2>

            <div className="space-y-12 mb-12">
              {steps.map((step, index) => (
                <div key={index} className="flex gap-6 group">
                  <div className="flex-shrink-0 w-14 h-14 bg-slate-50 border border-slate-200 flex items-center justify-center text-cyan-600 group-hover:border-cyan-300 transition-all">
                    <SafeIcon icon={step.icon} className="text-2xl" />
                  </div>
                  <div>
                    <h3 className="text-slate-900 font-bold text-lg mb-2">{step.title}</h3>
                    <p className="text-slate-600 leading-relaxed text-sm md:text-base">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-slate-50 border border-slate-200 p-8 space-y-4">
              {reassurances.map((text, index) => (
                <div key={index} className="flex items-center gap-3 text-slate-700 font-medium">
                  <SafeIcon icon={FiCheck} className="text-cyan-600 text-xl flex-shrink-0" />
                  <span>{text}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Right — Request a Call form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="bg-white border border-slate-200 shadow-sm relative overflow-hidden"
          >
            {/* Accent bar */}
            <div className="absolute top-0 left-0 w-full h-1 bg-cyan-500"></div>
            <div className="absolute top-0 right-0 w-40 h-40 bg-cyan-100/20 blur-3xl pointer-events-none"></div>

            <AnimatePresence mode="wait">
              {submitted ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="p-16 text-center"
                >
                  <div className="w-16 h-16 bg-cyan-50 border border-cyan-200 flex items-center justify-center mx-auto mb-6">
                    <SafeIcon icon={FiCheck} className="text-3xl text-cyan-500" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">Request Received</h3>
                  <p className="text-slate-600 leading-relaxed">
                    Thanks — we'll reach out within a few hours to confirm your call. Keep an eye on <span className="font-semibold text-slate-800">{form.email}</span>.
                  </p>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  onSubmit={handleSubmit}
                  className="p-8 md:p-10"
                >
                  <div className="mb-8">
                    <p className="text-slate-500 text-xs font-bold uppercase tracking-widest mb-1">Request a Call</p>
                    <div className="h-0.5 w-12 bg-cyan-500 mt-2"></div>
                  </div>

                  {/* Name + Email */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                    <div>
                      <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">Full Name *</label>
                      <div className="relative">
                        <SafeIcon icon={FiUser} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 text-base pointer-events-none" />
                        <input
                          type="text"
                          name="name"
                          required
                          value={form.name}
                          onChange={handleChange}
                          placeholder="Jane Smith"
                          className="w-full pl-9 pr-4 py-3 bg-slate-50 border border-slate-200 text-slate-900 text-sm placeholder:text-slate-300 focus:outline-none focus:border-cyan-400 transition-colors"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">Email *</label>
                      <div className="relative">
                        <SafeIcon icon={FiMail} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 text-base pointer-events-none" />
                        <input
                          type="email"
                          name="email"
                          required
                          value={form.email}
                          onChange={handleChange}
                          placeholder="jane@company.com"
                          className="w-full pl-9 pr-4 py-3 bg-slate-50 border border-slate-200 text-slate-900 text-sm placeholder:text-slate-300 focus:outline-none focus:border-cyan-400 transition-colors"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Company */}
                  <div className="mb-5">
                    <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">Company / Project <span className="text-slate-300 normal-case font-normal">(optional)</span></label>
                    <input
                      type="text"
                      name="company"
                      value={form.company}
                      onChange={handleChange}
                      placeholder="Acme Inc."
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 text-slate-900 text-sm placeholder:text-slate-300 focus:outline-none focus:border-cyan-400 transition-colors"
                    />
                  </div>

                  {/* Preferred time slot */}
                  <div className="mb-5">
                    <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-3">
                      <span className="inline-flex items-center gap-1.5"><SafeIcon icon={FiClock} className="text-cyan-500" /> Preferred Time of Day *</span>
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {TIME_SLOTS.map(slot => (
                        <button
                          type="button"
                          key={slot}
                          onClick={() => setForm(prev => ({ ...prev, timeSlot: slot }))}
                          className={`px-3 py-2.5 text-xs font-bold uppercase tracking-wider border transition-all text-left ${form.timeSlot === slot
                              ? 'bg-cyan-500 border-cyan-500 text-white'
                              : 'bg-slate-50 border-slate-200 text-slate-500 hover:border-cyan-300 hover:text-slate-700'
                            }`}
                        >
                          {slot}
                        </button>
                      ))}
                    </div>
                    {/* Hidden required validation proxy */}
                    <input
                      type="text"
                      required
                      value={form.timeSlot}
                      onChange={() => { }}
                      className="sr-only"
                      tabIndex={-1}
                    />
                  </div>

                  {/* Preferred days */}
                  <div className="mb-5">
                    <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-3">Preferred Days <span className="text-slate-300 normal-case font-normal">(pick any)</span></label>
                    <div className="flex flex-wrap gap-2">
                      {DAYS.map(day => (
                        <button
                          type="button"
                          key={day}
                          onClick={() => toggleDay(day)}
                          className={`px-3 py-1.5 text-[10px] font-black uppercase tracking-widest border transition-all ${form.preferredDays.includes(day)
                              ? 'bg-slate-900 border-slate-900 text-white'
                              : 'bg-slate-50 border-slate-200 text-slate-400 hover:border-slate-400 hover:text-slate-600'
                            }`}
                        >
                          {day}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Message */}
                  <div className="mb-7">
                    <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">What's the challenge? <span className="text-slate-300 normal-case font-normal">(optional)</span></label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      rows={3}
                      placeholder="Give us a quick summary so we can make the most of the call."
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 text-slate-900 text-sm placeholder:text-slate-300 focus:outline-none focus:border-cyan-400 transition-colors resize-none"
                    />
                  </div>

                  {error && (
                    <div className="mb-5 px-4 py-3 bg-red-50 border border-red-200 text-red-600 text-sm">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-4 bg-slate-900 text-white font-black uppercase tracking-widest text-xs hover:bg-black transition-all flex items-center justify-center gap-3 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? (
                      <>
                        <span>Sending…</span>
                        <SafeIcon icon={FiLoader} className="text-lg animate-spin" />
                      </>
                    ) : (
                      <>
                        <span>Request My Call</span>
                        <SafeIcon icon={FiArrowRight} className="text-lg" />
                      </>
                    )}
                  </button>

                  <p className="text-center text-slate-400 text-xs mt-5">
                    We'll confirm a time with you by email within a few hours.
                  </p>
                </motion.form>
              )}
            </AnimatePresence>
          </motion.div>
        </div>

        {/* Footer note */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.8 }}
          className="text-center mt-20 pt-10 border-t border-slate-100"
        >
          <p className="text-slate-500 text-sm">
            Prefer to reach us directly? Email{' '}
            <a
              href="mailto:hello@aetrixlogic.com"
              className="text-cyan-600 hover:text-slate-900 transition-colors font-bold"
            >
              hello@aetrixlogic.com
            </a>{' '}
            and we'll arrange a time manually.
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default BookCall;